import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import SubscriptionBanner from "@/components/SubscriptionBanner";
import { LayoutDashboard, MessageSquare, Wrench, BookOpen, Bookmark, LogOut, Flame, Heart, MapPin, HeartCrack, ShieldCheck } from "lucide-react";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/coach", label: "AI Coach", icon: MessageSquare, testid: "nav-coach" },
  { to: "/tools", label: "Tools", icon: Wrench, testid: "nav-tools" },
  { to: "/date-plan", label: "Date Plan", icon: MapPin, testid: "nav-date-plan" },
  { to: "/playbook/first-date", label: "Playbook", icon: Heart, testid: "nav-playbook" },
  { to: "/recovery", label: "Recovery", icon: HeartCrack, testid: "nav-recovery" },
  { to: "/library", label: "Library", icon: BookOpen, testid: "nav-library" },
  { to: "/saved", label: "Saved", icon: Bookmark, testid: "nav-saved" },
];

const AppShell = ({ children }) => {
  const { user, access, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex grain">
      {/* Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 flex-col border-r border-white/5 bg-[#0c0c0e] z-20">
        <div className="p-8">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <Flame className="w-6 h-6 text-amber-500" />
            <span className="font-serif text-2xl tracking-tight">Suitor</span>
          </Link>
          <p className="overline mt-1 text-[10px] text-neutral-500">MEN'S DATING PLAYBOOK</p>
        </div>
        <nav className="flex-1 px-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors ${
                  isActive
                    ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                    : "text-neutral-400 hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              <span>{item.label}</span>
            </NavLink>
          ))}
          {access?.is_admin && (
            <NavLink
              to="/admin/codes"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-colors mt-4 border-t border-white/5 pt-5 ${
                  isActive ? "text-amber-400" : "text-neutral-500 hover:text-white"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" />
              <span>Admin</span>
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-semibold" data-testid="user-avatar">
              {user?.name?.[0]?.toUpperCase() || "S"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-neutral-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-neutral-400 hover:text-white hover:bg-white/5 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 glass-nav z-30 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Flame className="w-5 h-5 text-amber-500" />
          <span className="font-serif text-xl">Suitor</span>
        </Link>
        <button onClick={handleLogout} className="text-sm text-neutral-400" data-testid="mobile-logout-btn">
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 glass-nav flex justify-around py-2">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            data-testid={`m-${item.testid}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1 text-xs ${
                isActive ? "text-amber-400" : "text-neutral-500"
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[10px]">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Main */}
      <main className="flex-1 md:ml-64 pt-16 md:pt-0 pb-24 md:pb-8 relative z-10">
        <SubscriptionBanner />
        {children}
      </main>
    </div>
  );
};

export default AppShell;
